import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Ban, HeartPulse, Plus, RotateCcw } from 'lucide-react';
import { VITALS_NOT_TAKEN_REASON_MAX, VITALS_NOT_TAKEN_REASON_MIN } from '@his/shared';
import { put } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { tr } from '@/lib/i18n-core';
import { cn, dateTime } from '@/lib/utils';
import { DataTable, type DataTableColumn } from '@/components/ui/data-table';
import { Badge, Button } from '@/components/ui/primitives';
import { ReasonDialog } from '@/components/ui/reason-dialog';
import { useToast } from '@/components/ui/toast';
import type { Encounter, EncounterVitals } from './types';
import { nextVitalsAreRecheck, vitalReadings } from './vitals';
import { VitalTile } from './VitalTile';
import { EmrEmpty, EmrPanel } from './EmrPanel';

function readingCell(row: EncounterVitals, key: string) {
  const reading = vitalReadings(row, { all: true }).find((candidate) => candidate.key === key);
  if (!reading?.value) return <span className="text-muted-foreground/50">—</span>;
  return (
    <span className={cn('tabular', reading.flag && 'font-semibold text-destructive')}>
      {reading.value}
      {reading.unit && <span className="ms-0.5 text-[11px] font-normal text-muted-foreground">{reading.unit}</span>}
    </span>
  );
}

function RecheckNote({ reason }: { reason?: string | null }) {
  return (
    <span className="mt-1 flex min-w-0 max-w-[16rem] items-center gap-1.5 text-xs text-muted-foreground">
      <Badge variant="warning" className="shrink-0 gap-1">
        <RotateCcw className="h-3 w-3" aria-hidden /> {tr('opd', 'emr_recheck_badge')}
      </Badge>
      {reason && (
        <span className="truncate" title={reason}>
          {reason}
        </span>
      )}
    </span>
  );
}

function NotTakenNote({ encounter, canEdit }: { encounter: Encounter; canEdit: boolean }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [asking, setAsking] = useState(false);
  const save = useMutation({
    mutationFn: (reason: string | null) => put(`/encounters/${encounter.id}`, { vitalsNotTakenReason: reason }),
    onSuccess: () => {
      setAsking(false);
      void queryClient.invalidateQueries({ queryKey: ['encounter', encounter.id] });
    },
    onError: (error) => toast.error(tr('opd', 'could_not_save'), describeError(error)),
  });
  const reason = encounter.vitalsNotTakenReason;
  return (
    <>
      {reason ? (
        <div className="flex flex-wrap items-center gap-2 rounded-xl border border-info/30 bg-info/10 px-3 py-2 text-sm">
          <Ban className="h-4 w-4 shrink-0 text-info" aria-hidden />
          <span className="min-w-0 flex-1">{tr('opd', 'emr_vitals_not_taken_x', { reason })}</span>
          {canEdit && (
            <Button size="sm" variant="ghost" loading={save.isPending} onClick={() => save.mutate(null)}>
              {tr('opd', 'emr_vitals_not_taken_clear')}
            </Button>
          )}
        </div>
      ) : (
        canEdit && (
          <Button size="sm" variant="ghost" onClick={() => setAsking(true)}>
            <Ban className="h-4 w-4" aria-hidden /> {tr('opd', 'emr_vitals_not_taken')}
          </Button>
        )
      )}
      {asking && (
        <ReasonDialog
          title={tr('opd', 'emr_vitals_not_taken_title')}
          description={tr('opd', 'emr_vitals_not_taken_prompt')}
          minLength={VITALS_NOT_TAKEN_REASON_MIN}
          maxLength={VITALS_NOT_TAKEN_REASON_MAX}
          submitLabel={tr('opd', 'save')}
          loading={save.isPending}
          onSubmit={(text) => save.mutate(text)}
          onClose={() => setAsking(false)}
        />
      )}
    </>
  );
}

export function VitalsSection({ encounter, onRecord, canMarkNotTaken = false }: { encounter: Encounter; onRecord?: () => void; canMarkNotTaken?: boolean }) {
  const latest = encounter.vitals[0];
  const recordLabel = nextVitalsAreRecheck(encounter) ? tr('opd', 'vitals_recheck') : tr('opd', 'record_vitals');
  const columns: DataTableColumn<EncounterVitals>[] = [
    {
      header: tr('opd', 'emr_taken_at'),
      sort: (row) => row.recordedAt,
      cell: (row) => (
        <div className="min-w-0">
          <span className="whitespace-nowrap">{dateTime(row.recordedAt)}</span>
          {row.isRecheck && <RecheckNote reason={row.recheckReason} />}
        </div>
      ),
    },
    { header: tr('opd', 'vital_bp'), cell: (row) => readingCell(row, 'bp') },
    { header: tr('opd', 'vital_pulse'), cell: (row) => readingCell(row, 'pulse') },
    { header: tr('opd', 'vital_temperature'), cell: (row) => readingCell(row, 'temperature') },
    { header: tr('opd', 'vital_spo2'), cell: (row) => readingCell(row, 'spo2') },
    { header: tr('opd', 'vital_resp_rate'), cell: (row) => readingCell(row, 'respiratoryRate'), hideBelow: 'md' },
    { header: tr('opd', 'vital_weight'), cell: (row) => readingCell(row, 'weight'), hideBelow: 'md' },
    { header: tr('opd', 'vital_bmi'), cell: (row) => readingCell(row, 'bmi'), hideBelow: 'lg' },
    { header: tr('opd', 'vital_sugar'), cell: (row) => readingCell(row, 'sugar'), hideBelow: 'lg' },
    {
      header: '',
      cell: (row) => (row.isAbnormal ? <Badge variant="destructive">{tr('opd', 'abnormal_values')}</Badge> : null),
    },
  ];

  return (
    <div className="space-y-4">
      <EmrPanel
        icon={HeartPulse}
        title={tr('opd', 'emr_vitals')}
        description={latest ? tr('opd', 'emr_latest_reading_x', { time: dateTime(latest.recordedAt) }) : tr('opd', 'emr_no_vitals_yet')}
        actions={
          onRecord && (
            <Button size="sm" onClick={onRecord}>
              <Plus className="h-4 w-4" aria-hidden /> {recordLabel}
            </Button>
          )
        }
      >
        {latest ? (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 xl:grid-cols-5">
              {vitalReadings(latest, { all: true }).map((reading) => (
                <VitalTile key={reading.key} reading={reading} />
              ))}
            </div>
            {latest.isRecheck && (
              <p className="flex items-start gap-2 rounded-xl border border-warning/30 bg-warning/10 px-3 py-2 text-sm">
                <RotateCcw className="mt-0.5 h-4 w-4 shrink-0 text-warning" aria-hidden />
                <span>{tr('opd', 'emr_recheck_reason_x', { reason: latest.recheckReason ?? '' })}</span>
              </p>
            )}
            {latest.notes && <p className="rounded-xl bg-muted/40 px-3 py-2 text-sm text-muted-foreground">{latest.notes}</p>}
          </div>
        ) : (
          <div className="space-y-3">
            <EmrEmpty
              action={
                onRecord && (
                  <Button size="sm" variant="outline" onClick={onRecord}>
                    {tr('opd', 'record_vitals')}
                  </Button>
                )
              }
            >
              {tr('opd', 'emr_no_vitals_yet')}
            </EmrEmpty>
            <NotTakenNote encounter={encounter} canEdit={canMarkNotTaken} />
          </div>
        )}
      </EmrPanel>

      {encounter.vitals.length > 1 && (
        <EmrPanel icon={HeartPulse} title={tr('opd', 'emr_vitals_history')} bodyClassName="p-0">
          <DataTable rows={encounter.vitals} columns={columns} rowKey={(row) => row.id} dense defaultSort={{ column: tr('opd', 'emr_taken_at'), direction: 'desc' }} />
        </EmrPanel>
      )}
    </div>
  );
}
